<template>
  <div v-if="build">
    <div class="flex items-center justify-between mb-6">
      <div>
        <button @click="$emit('back')" class="text-sm text-gray-500 hover:text-gray-700 mb-2">
          &larr; Back to Builds
        </button>
        <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {{ build.target_slug }}
        </h2>
        <div class="flex items-center gap-3 mt-1 text-sm text-gray-500 dark:text-gray-400">
          <span :class="statusClass(build.build_status)" class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium">
            {{ build.build_status }}
          </span>
          <span>Template: {{ build.template_name }}</span>
        </div>
      </div>
      <div class="flex gap-2">
        <button v-if="build.build_status === 'review'" @click="handleApprove" class="btn btn-cta btn-sm">Approve</button>
        <button v-if="build.build_status === 'review'" @click="showRejectForm = true" class="btn btn-secondary btn-sm text-red-600">Reject</button>
        <button v-if="build.build_status === 'approved'" @click="handlePublish" :disabled="publishing" class="btn btn-cta btn-sm">
          {{ publishing ? 'Publishing...' : 'Publish' }}
        </button>
      </div>
    </div>

    <!-- Preview -->
    <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden mb-6">
      <div class="p-4 border-b border-gray-200 dark:border-gray-700">
        <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100">Page Preview</h3>
      </div>
      <div class="p-4">
        <div v-if="build.rendered_html" v-html="build.rendered_html" class="prose max-w-none"></div>
        <p v-else class="text-sm text-gray-500">Page not yet rendered</p>
      </div>
    </div>

    <!-- SEO Metadata -->
    <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden mb-6">
      <div class="p-4 border-b border-gray-200 dark:border-gray-700">
        <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100">SEO Metadata</h3>
      </div>
      <div class="p-4">
        <dl v-if="build.seo_metadata" class="space-y-3">
          <div v-if="build.seo_metadata.title">
            <dt class="text-xs text-gray-500">Title</dt>
            <dd class="text-sm text-gray-900 dark:text-gray-100">{{ build.seo_metadata.title }}</dd>
          </div>
          <div v-if="build.seo_metadata.meta_description">
            <dt class="text-xs text-gray-500">Meta Description</dt>
            <dd class="text-sm text-gray-900 dark:text-gray-100">{{ build.seo_metadata.meta_description }}</dd>
          </div>
          <div v-if="build.seo_metadata.canonical">
            <dt class="text-xs text-gray-500">Canonical URL</dt>
            <dd class="text-sm text-primary-600">{{ build.seo_metadata.canonical }}</dd>
          </div>
        </dl>
        <p v-else class="text-sm text-gray-500">SEO metadata not yet generated</p>
      </div>
    </div>

    <!-- Review Notes -->
    <div v-if="build.review_notes" class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden mb-6">
      <div class="p-4 border-b border-gray-200 dark:border-gray-700">
        <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100">Review Notes</h3>
      </div>
      <div class="p-4 text-sm text-gray-700 dark:text-gray-300">
        {{ build.review_notes }}
      </div>
    </div>

    <!-- Reject form -->
    <UiSlidePanel v-if="showRejectForm" title="Reject Page Build" @close="showRejectForm = false">
      <form @submit.prevent="handleReject">
        <UiTextarea v-model="rejectNotes" label="Rejection Notes" placeholder="Explain what needs to change..." required />
        <div class="mt-6 flex justify-end gap-3">
          <button type="button" @click="showRejectForm = false" class="btn btn-secondary btn-sm">Cancel</button>
          <button type="submit" class="btn btn-sm bg-red-600 text-white hover:bg-red-700">Reject</button>
        </div>
      </form>
    </UiSlidePanel>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { getPageBuild, approvePageBuild, rejectPageBuild, publishPageBuild } from '~/javascript/api/page_builds'

const props = defineProps({
  buildId: { type: String, required: true }
})

const emit = defineEmits(['back', 'updated'])

const build = ref(null)
const publishing = ref(false)
const showRejectForm = ref(false)
const rejectNotes = ref('')

const statusClass = (status) => {
  const classes = {
    draft: 'bg-gray-100 text-gray-600',
    generating: 'bg-blue-100 text-blue-700',
    review: 'bg-yellow-100 text-yellow-700',
    approved: 'bg-green-100 text-green-700',
    published: 'bg-primary-100 text-primary-700',
    rejected: 'bg-red-100 text-red-700'
  }
  return classes[status] || classes.draft
}

const fetchBuild = async () => {
  const { data } = await getPageBuild(props.buildId)
  build.value = data
}

const handleApprove = async () => {
  await approvePageBuild(props.buildId)
  await fetchBuild()
  emit('updated')
}

const handleReject = async () => {
  await rejectPageBuild(props.buildId, rejectNotes.value)
  showRejectForm.value = false
  rejectNotes.value = ''
  await fetchBuild()
  emit('updated')
}

const handlePublish = async () => {
  publishing.value = true
  try {
    await publishPageBuild(props.buildId)
    await fetchBuild()
    emit('updated')
  } finally {
    publishing.value = false
  }
}

onMounted(fetchBuild)
</script>
